import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './lib/auth';
import PageLoader from './components/PageLoader';
import './App.css';

// Layouts
import PublicLayout from './components/PublicLayout';
import AdminLayout from './components/AdminLayout';

// Public Pages
import HomePage from './pages/public/HomePage';
import ServicesPage from './pages/public/ServicesPage';
import CitiesPage from './pages/public/CitiesPage';
import CityPage from './pages/public/CityPage';
import LandingPage from './pages/public/LandingPage';
import QuoteFormPage from './pages/public/QuoteFormPage';
import BlogListPage from './pages/public/BlogListPage';
import BlogDetailPage from './pages/public/BlogDetailPage';
import ContactPage from './pages/public/ContactPage';
import ThankYouPage from './pages/public/ThankYouPage';
import LegalPage from './pages/public/LegalPage';
import AboutPage from './pages/public/AboutPage';

// Admin Pages
import LoginPage from './pages/admin/LoginPage';
import DashboardPage from './pages/admin/DashboardPage';
import LeadsPage from './pages/admin/LeadsPage';
import LeadDetailPage from './pages/admin/LeadDetailPage';
import CompaniesPage from './pages/admin/CompaniesPage';
import ServicesAdminPage from './pages/admin/ServicesAdminPage';
import CitiesAdminPage from './pages/admin/CitiesAdminPage';
import LandingPagesPage from './pages/admin/LandingPagesPage';
import BlogAdminPage from './pages/admin/BlogAdminPage';
import CMSPage from './pages/admin/CMSPage';
import MediaPage from './pages/admin/MediaPage';
import SettingsPage from './pages/admin/SettingsPage';
import AuditLogsPage from './pages/admin/AuditLogsPage';
import UsersPage from './pages/admin/UsersPage';
import DirectoryAdminPage from './pages/admin/DirectoryAdminPage';
import ContentTemplatePage from './pages/admin/ContentTemplatePage';
import ProfilePage from './pages/admin/ProfilePage';

// Directory Public Pages
import DirectoryPage from './pages/public/DirectoryPage';
import DirectoryCategoryPage from './pages/public/DirectoryCategoryPage';
import DirectoryCompanyPage from './pages/public/DirectoryCompanyPage';

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(221,83%,22%)]"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/hizmetler" element={<PublicLayout><ServicesPage /></PublicLayout>} />
      <Route path="/sehirler" element={<PublicLayout><CitiesPage /></PublicLayout>} />
      <Route path="/blog" element={<PublicLayout><BlogListPage /></PublicLayout>} />
      <Route path="/blog/:slug" element={<PublicLayout><BlogDetailPage /></PublicLayout>} />
      <Route path="/teklif-al" element={<PublicLayout><QuoteFormPage /></PublicLayout>} />
      <Route path="/iletisim" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/tesekkurler" element={<PublicLayout><ThankYouPage /></PublicLayout>} />
      <Route path="/kvkk" element={<PublicLayout><LegalPage /></PublicLayout>} />
      <Route path="/aydinlatma-metni" element={<PublicLayout><LegalPage /></PublicLayout>} />
      <Route path="/cerez-politikasi" element={<PublicLayout><LegalPage /></PublicLayout>} />
      <Route path="/sorumluluk-metni" element={<PublicLayout><LegalPage /></PublicLayout>} />
      <Route path="/hakkimizda" element={<PublicLayout><AboutPage /></PublicLayout>} />

      {/* Directory Routes */}
      <Route path="/sanayi-rehberi" element={<PublicLayout><DirectoryPage /></PublicLayout>} />
      <Route path="/sanayi-rehberi/:categorySlug" element={<PublicLayout><DirectoryCategoryPage /></PublicLayout>} />
      <Route path="/sanayi-rehberi/:categorySlug/:companySlug" element={<PublicLayout><DirectoryCompanyPage /></PublicLayout>} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/admin" element={<ProtectedRoute><AdminLayout><DashboardPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute><AdminLayout><LeadsPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/leads/:id" element={<ProtectedRoute><AdminLayout><LeadDetailPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/companies" element={<ProtectedRoute><AdminLayout><CompaniesPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/services" element={<ProtectedRoute><AdminLayout><ServicesAdminPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/cities" element={<ProtectedRoute><AdminLayout><CitiesAdminPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/landing-pages" element={<ProtectedRoute><AdminLayout><LandingPagesPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/blog" element={<ProtectedRoute><AdminLayout><BlogAdminPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/cms" element={<ProtectedRoute><AdminLayout><CMSPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/media" element={<ProtectedRoute><AdminLayout><MediaPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><AdminLayout><SettingsPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/audit-logs" element={<ProtectedRoute><AdminLayout><AuditLogsPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminLayout><UsersPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/directory" element={<ProtectedRoute><AdminLayout><DirectoryAdminPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/templates" element={<ProtectedRoute><AdminLayout><ContentTemplatePage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/profile" element={<ProtectedRoute><AdminLayout><ProfilePage /></AdminLayout></ProtectedRoute>} />

      {/* SEO Dynamic Routes - Must be last */}
      <Route path="/:citySlug/:serviceSlug" element={<PublicLayout><LandingPage /></PublicLayout>} />
      <Route path="/:citySlug" element={<PublicLayout><CityPage /></PublicLayout>} />
    </Routes>
  );
}

import { useEffect } from 'react';

function BrandingCleaner() {
  useEffect(() => {
    const remove = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.remove();
      document.querySelectorAll('a[href*="emergent.sh"], [id*="emergent"], [class*="emergent"]').forEach(n => n.remove());
    };
    remove();
    const obs = new MutationObserver(remove);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <BrandingCleaner />
          <PageLoader />
          <AppRoutes />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
